import { Howl, Howler } from 'howler';
import { isMusicMuted, isSfxMuted, subscribeMusicMuted } from '../lib/audioSettings.js';
import type { AudioConfig } from '../types.js';

export type SoundEffect = 'click' | 'questOpen' | 'questAnswer' | 'victory' | 'gameOver';


export class AudioManager {
    private readonly howls = new Map<string, Howl>();
    private playingMusic: string | null = null;
    private readonly aliasPrefix: string;
    private unsubscribeMusicMute: (() => void) | null = null;

    constructor(
        private readonly assetsPath: string,
        private readonly config?: AudioConfig,
        gameId = 'default',
    ) {
        this.aliasPrefix = `game:${gameId}:`;
    }

    register(): void {
        if (!this.config) return;

        this.add('music:title', this.config.music?.title, true);
        this.add('music:background', this.config.background, true);
        Object.entries(this.config.music?.stages ?? {}).forEach(([stage, source]) => {
            this.add(`music:stage:${stage}`, source, true);
        });
        (Object.keys(this.config.sfx ?? {}) as SoundEffect[]).forEach((effect) => {
            this.add(`sfx:${effect}`, this.config?.sfx?.[effect], false);
        });

        // Music mute is per-Howl: a global Howler.mute would silence SFX too.
        this.applyMusicMute(isMusicMuted());
        this.unsubscribeMusicMute = subscribeMusicMuted((muted) => this.applyMusicMute(muted));
    }

    playTitle(): void {
        this.playMusic('music:title');
    }

    playOpening(): void {
        this.unlockAudioContext();
        if (this.howls.has(this.alias('music:title'))) {
            this.playTitle();
            return;
        }
        const firstStage = Object.keys(this.config?.music?.stages ?? {})[0];
        this.playStage(firstStage ?? '1');
    }

    playStage(stage: string): void {
        this.playMusic(`music:stage:${stage}`);
    }

    playEffect(effect: SoundEffect): void {
        if (isSfxMuted()) return;
        const howl = this.howls.get(this.alias(`sfx:${effect}`));
        if (howl) howl.play();
    }

    stopMusic(): void {
        if (this.playingMusic) {
            this.howls.get(this.playingMusic)?.stop();
            this.playingMusic = null;
        }
    }

    pauseMusic(): void {
        if (this.playingMusic) this.howls.get(this.playingMusic)?.pause();
    }

    resumeMusic(): void {
        if (!this.playingMusic) return;
        this.unlockAudioContext();
        const howl = this.howls.get(this.playingMusic);
        if (howl && !howl.playing()) howl.play();
    }

    destroy(): void {
        this.unsubscribeMusicMute?.();
        this.unsubscribeMusicMute = null;
        this.stopMusic();
        this.howls.forEach((howl) => howl.unload());
        this.howls.clear();
    }

    private applyMusicMute(muted: boolean): void {
        this.howls.forEach((howl, alias) => {
            if (alias.includes(':music:')) howl.mute(muted);
        });
    }

    private add(alias: string, source: string | undefined, loop: boolean): void {
        if (!source || this.howls.has(this.alias(alias))) return;

        const qualifiedAlias = this.alias(alias);
        const howl = new Howl({
            src: [this.assetsPath + source],
            loop,
            preload: true,
            onloaderror: (_soundId, error) => {
                console.warn(`Could not load audio ${qualifiedAlias}:`, error);
            },
        });
        this.howls.set(qualifiedAlias, howl);
    }

    private playMusic(alias: string): void {
        const qualifiedAlias = this.alias(alias);
        const fallback = alias.startsWith('music:stage:')
            ? this.alias('music:background')
            : null;
        const playableAlias = this.howls.has(qualifiedAlias)
            ? qualifiedAlias
            : fallback && this.howls.has(fallback) ? fallback : null;
        if (!playableAlias || this.playingMusic === playableAlias) return;

        this.stopMusic();
        this.howls.get(playableAlias)?.play();
        this.playingMusic = playableAlias;
    }

    private alias(name: string): string {
        return this.aliasPrefix + name;
    }

    private unlockAudioContext(): void {
        const context = Howler.ctx;
        if (!context || context.state !== 'suspended') return;

        void context.resume().catch((error: unknown) => {
            console.warn('Could not unlock the audio context:', error);
        });
    }
}
